import { AnyBlock, ImageBlock, Point2D, Size } from "../types";
import {
  clampPosition,
  pixelToGridPosition,
  pixelToGridSize,
} from "./gridPosition";
import {
  MAX_IMG_WIDTH,
  MAX_IMG_HEIGHT,
  MAX_IMG_RESOLUTION_WIDTH,
  MAX_IMG_RESOLUTION_HEIGHT,
  DEFAULT_GRID_RESOLUTION,
} from "../consts";
import * as StackBlur from "stackblur-canvas";
import fitRect from "fit-rect";
import createBlock from "../blocks/createBlock";
import { v4 as uuidv4 } from "uuid";
import { captureEvent, Severity } from "@sentry/react";
import { AnalyticEvent } from "../analytics/events";

/**
 * upload a blob or file to S3
 * @param blob
 * @param key - the S3 Object key, which defines the path of upload file
 * @returns url of the upload, i.e. "https://file.sprout.place/{key}"
 */
export async function upload(blob: Blob | File, key?: string) {
  if (!key) {
    key = `pastes/${uuidv4()}`;
  }

  let res = await fetch("/api/request-upload", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      contentType: blob.type,
      key,
    }),
  });

  if (!res.ok) {
    throw new Error("fail to request upload url");
  }

  const { objectURL, uploadURL } = await res.json();
  res = await fetch(uploadURL, {
    method: "PUT",
    body: blob,
    headers: {
      // we treat each uploaded file as immutable object that cannot
      // be overwritten. so each object can be cached forever
      // max-age = a year
      "Cache-Control": "max-age=31536000",
    },
  });

  if (!res.ok) {
    const error = await res.text();
    captureEvent({
      message: "S3 upload failure",
      level: Severity.Error,
      extra: { body: error, res },
    });
    throw new Error("s3 upload failed");
  }

  return objectURL;
}

export function handleScreenshot(
  blob: Blob,
  upsertBlock: (b: Partial<AnyBlock>) => void,
  blockPosition: Point2D,
  sizePixels?: Size,
  blur = true
) {
  const dataUrl = URL.createObjectURL(blob);
  const imageObj = new Image();
  imageObj.onload = () => {
    const previewUrl = createPreviewDataURL(imageObj, blur);
    const w = sizePixels?.width || imageObj.width;
    const h = sizePixels?.height || imageObj.height;
    const block: ImageBlock = createBlock(
      "image",
      clampPosition(blockPosition),
      pixelToGridSize(
        {
          width: w,
          height: h,
        },
        Math.round
      )
    ) as ImageBlock;
    if (!block) return;

    upsertBlock({ ...block, value: previewUrl });

    upload(blob).then(
      (url) => {
        upsertBlock({ id: block.id, value: url });
      },
      () => {}
    );
  };
  imageObj.src = dataUrl;
}

export function createPreviewDataURL(img: HTMLImageElement, blur = true) {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    return;
  }

  const aspect = img.width / img.height;

  canvas.width = 32 * aspect;
  canvas.height = 32;

  context.drawImage(img, 0, 0, canvas.width, canvas.height);
  if (blur) StackBlur.canvasRGBA(canvas, 0, 0, canvas.width, canvas.height, 3);

  return canvas.toDataURL();
}

export function capImageSize(img: HTMLImageElement, file: File): Promise<Blob> {
  // skip resizing for gifs, they stop being animated with the current method
  const isGif = file.type === "image/gif";
  if (isGif) {
    return Promise.resolve(file);
  }

  if (
    img.width < MAX_IMG_RESOLUTION_WIDTH &&
    img.height < MAX_IMG_RESOLUTION_HEIGHT
  ) {
    // no need to resize, upload as is
    return Promise.resolve(file);
  }

  const rect = [0, 0, img.width, img.height];
  const target = [0, 0, MAX_IMG_RESOLUTION_WIDTH, MAX_IMG_RESOLUTION_HEIGHT];
  const contained = fitRect(rect, target, "contain");

  const targetWidth = contained[2];
  const targetHeight = contained[3];

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    return Promise.reject();
  }

  canvas.width = targetWidth;
  canvas.height = targetHeight;
  context.drawImage(img, 0, 0, targetWidth, targetHeight);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject();
      }
    });
  });
}

export function handlePaste(
  dataTransfer: DataTransfer | null,
  upsertBlock: (b: Partial<AnyBlock>) => void,
  mousePosition: Point2D,
  sendAnalyticsEvent?: (userEvent: AnalyticEvent) => void
) {
  const items = dataTransfer?.items;
  if (!items) return;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!item.type.includes("image")) continue;

    const file = item.getAsFile();
    if (!file) continue;

    const image = new Image();

    image.addEventListener("load", function () {
      let { width, height } = this;
      // Cap size of inserted image block
      // (Image resolution is capped seperately)
      if (width > MAX_IMG_WIDTH || height > MAX_IMG_HEIGHT) {
        const rect = [0, 0, width, height];
        const target = [0, 0, MAX_IMG_WIDTH, MAX_IMG_HEIGHT];
        const contained = fitRect(rect, target, "contain");
        width = contained[2];
        height = contained[3];
      }

      const { position, size } = getImagePropertiesInGrid(
        width,
        height,
        mousePosition
      );

      const block = createBlock("image", position, size);

      const value = createPreviewDataURL(image);

      if (block) {
        upsertBlock({ ...block, size, value });
        if (sendAnalyticsEvent)
          sendAnalyticsEvent({
            name: "blockCreated",
            type: block.type,
            pasted: true,
          });
      }

      capImageSize(image, file)
        .then(upload)
        .then((url) => {
          if (block)
            upsertBlock({
              id: block.id,
              value: url,
            });
        })
        .catch(() => {});
    });
    image.src = URL.createObjectURL(file);
  }
}

export function handleDroppedImage(
  width: number,
  height: number,
  mousePositionRef: React.MutableRefObject<Point2D>,
  url: string,
  upsertBlock: (block: Partial<AnyBlock>) => void,
  sendAnalyticsEvent?: (userEvent: AnalyticEvent) => void
) {
  /** Cap width to 8 grid units while keeping aspect ratio. */
  const aspectRatio = width / height;
  const cappedWidth = Math.min(8 * DEFAULT_GRID_RESOLUTION, width);
  const cappedHeight = cappedWidth / aspectRatio;

  const { position, size } = getImagePropertiesInGrid(
    cappedWidth,
    cappedHeight,
    mousePositionRef.current
  );

  const block = createBlock("image", position, size, url);
  if (block) {
    upsertBlock(block);
    if (sendAnalyticsEvent)
      sendAnalyticsEvent({
        name: "blockCreated",
        type: block.type,
        dropped: true,
      });
  }
}

function getImagePropertiesInGrid(
  pixelWidth: number,
  pixelHeight: number,
  mousePosition: Point2D
): { position: Point2D; size: Size } {
  const gridSize = pixelToGridSize({
    width: pixelWidth,
    height: pixelHeight,
  });

  return {
    position: clampPosition(
      pixelToGridPosition({
        x: mousePosition.x - (gridSize.width * DEFAULT_GRID_RESOLUTION) / 2,
        y: mousePosition.y - (gridSize.height * DEFAULT_GRID_RESOLUTION) / 2,
      })
    ),
    size: gridSize,
  };
}
